import { useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { getDeviceCategoryLabel, getEfemPeripheralReassignState } from '@cnapse/shared';
import * as dt from '@cnapse/shared/data.types.js';
import type { DeviceInventoryStatus, TenantDeviceRecord } from '@cnapse/shared/schemas.js';
import { AppButton } from '../../design-system';
import { EfemSetupList, EfemSetupListEmpty, EfemSetupListRow } from './EfemSetupList';
import { EfemSetupSection } from './EfemSetupSection';
import { MAX_EFEM_LOAD_PORTS } from './efemSetup.constants';
import type { LoadPortRow } from './efemGatewaySetup.types';

export type { LoadPortRow };

interface LoadPortsSectionProps {
  gatewaySerialNumber: string;
  rows: LoadPortRow[];
  onRowsChange: (rows: LoadPortRow[]) => void;
  allScanners: TenantDeviceRecord[];
  tenantGatewayStatusBySerial: ReadonlyMap<string, DeviceInventoryStatus | undefined>;
  disabled: boolean;
  loading?: boolean;
}

const LOAD_PORT_SIZES = Object.values(dt.EFEM_LOAD_PORT_SIZE);

const EMPTY_FORM: LoadPortRow = {
  id: '',
  size: dt.EFEM_LOAD_PORT_SIZE.Size300mm,
  scannerSerial: '',
};

function getScannerAssignedLoadPort(
  rows: LoadPortRow[],
  scannerSerial: string,
  excludeId: string | null,
): string | null {
  const other = rows.find((r) => r.id !== excludeId && r.scannerSerial === scannerSerial);
  return other?.id ?? null;
}

function scannerSubtitle(
  serial: string,
  allScanners: TenantDeviceRecord[],
): string {
  if (!serial) return 'No fixed barcode module';
  const scanner = allScanners.find((s) => s.serialNumber === serial);
  return scanner?.deviceModel ? `${serial} (${scanner.deviceModel})` : serial;
}

export function LoadPortsSection({
  gatewaySerialNumber,
  rows,
  onRowsChange,
  allScanners,
  tenantGatewayStatusBySerial,
  disabled,
  loading = false,
}: LoadPortsSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<LoadPortRow>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const barcodeModuleLabel = getDeviceCategoryLabel('barcode-scanner');
  const canAddMore = rows.length < MAX_EFEM_LOAD_PORTS;

  const getScannerReassignState = (scanner: TenantDeviceRecord) => {
    const assignedPort = getScannerAssignedLoadPort(rows, scanner.serialNumber, editingId);
    return getEfemPeripheralReassignState({
      parentGatewaySerial: scanner.parentGatewaySerialNumber,
      currentGatewaySerial: gatewaySerialNumber,
      tenantGatewayStatusBySerial,
      blockedByLocalDraft: assignedPort !== null,
      localDraftBlockLabel: assignedPort ? ` (load port ${assignedPort})` : undefined,
    });
  };

  const openAddDialog = () => {
    const nextId =
      Array.from({ length: MAX_EFEM_LOAD_PORTS }, (_, i) => i + 1).find(
        (n) => !rows.some((r) => r.id === String(n)),
      ) ?? 1;
    setEditingId(null);
    setForm({ id: String(nextId), size: dt.EFEM_LOAD_PORT_SIZE.Size300mm, scannerSerial: '' });
    setFormError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (row: LoadPortRow) => {
    setEditingId(row.id);
    setForm({ ...row });
    setFormError(null);
    setDialogOpen(true);
  };

  const applyDialogForm = () => {
    const portId = form.id.trim();
    const nextRows = editingId
      ? rows.map((r) => (r.id === editingId ? { ...form, id: editingId } : r))
      : [...rows, { ...form, id: portId }];
    onRowsChange(nextRows);
    setDialogOpen(false);
  };

  const handleDialogApply = () => {
    const portId = form.id.trim();
    if (!portId || Number.isNaN(Number(portId))) {
      setFormError('Port ID is required');
      return;
    }
    if (rows.some((r) => r.id === portId && r.id !== editingId)) {
      setFormError('Port ID must be unique');
      return;
    }
    if (form.scannerSerial) {
      const scanner = allScanners.find((s) => s.serialNumber === form.scannerSerial);
      if (scanner) {
        const reassign = getScannerReassignState(scanner);
        if (!reassign.selectable) {
          setFormError(`This ${barcodeModuleLabel.toLowerCase()} is already assigned${reassign.assignmentLabel}`);
          return;
        }
        if (reassign.requiresMoveConfirm) {
          const fromGateway = scanner.parentGatewaySerialNumber ?? 'another gateway';
          if (
            !window.confirm(
              `${form.scannerSerial} is currently commissioned on gateway ${fromGateway}. `
              + `Move it to load port ${portId} on this gateway when you save?`,
            )
          ) {
            return;
          }
        }
      }
    }

    applyDialogForm();
  };

  const handleDelete = (row: LoadPortRow) => {
    if (!window.confirm(`Remove load port ${row.id}?`)) return;
    onRowsChange(rows.filter((r) => r.id !== row.id));
  };

  const sortedRows = [...rows].sort((a, b) => Number(a.id) - Number(b.id));
  const scannerCount = rows.filter((r) => r.scannerSerial).length;
  const statusLabel =
    rows.length === 0
      ? undefined
      : `${rows.length} port${rows.length !== 1 ? 's' : ''}${scannerCount > 0 ? ` · ${scannerCount} scanner${scannerCount !== 1 ? 's' : ''}` : ''}`;

  return (
    <EfemSetupSection
      title="Load ports"
      statusLabel={statusLabel}
      statusVariant={rows.length > 0 ? 'success' : 'warning'}
      disabled={disabled}
      disabledHint="Enter a gateway name above to configure load ports."
      headerAction={
        canAddMore ? (
          <AppButton size="small" variant="contained" startIcon={<Add />} onClick={openAddDialog}>
            Add port
          </AppButton>
        ) : undefined
      }
    >
      {loading ? (
        <Box py={1}>
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            Loading…
          </Alert>
        </Box>
      ) : sortedRows.length === 0 ? (
        <EfemSetupListEmpty
          message="No load ports configured yet."
          action={
            <AppButton size="small" variant="outlined" startIcon={<Add />} onClick={openAddDialog}>
              Add load port
            </AppButton>
          }
        />
      ) : (
        <EfemSetupList>
          {sortedRows.map((row, index) => (
            <EfemSetupListRow
              key={row.id}
              primary={`Port ${row.id}`}
              secondary={scannerSubtitle(row.scannerSerial, allScanners)}
              meta={row.size}
              onEdit={() => openEditDialog(row)}
              editLabel="Edit load port"
              onDelete={() => handleDelete(row)}
              deleteLabel="Remove load port"
              isLast={index === sortedRows.length - 1}
            />
          ))}
        </EfemSetupList>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingId ? 'Edit load port' : 'Add load port'}</DialogTitle>
        <DialogContent sx={{ overflow: 'visible', pt: 2.5 }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Port ID</InputLabel>
              <Select
                label="Port ID"
                value={form.id}
                disabled={!!editingId}
                onChange={(e) => setForm((prev) => ({ ...prev, id: e.target.value }))}
              >
                {editingId ? (
                  <MenuItem value={form.id}>{form.id}</MenuItem>
                ) : (
                  Array.from({ length: MAX_EFEM_LOAD_PORTS }, (_, i) => i + 1).map((n) => (
                    <MenuItem key={n} value={String(n)} disabled={rows.some((r) => r.id === String(n))}>
                      {n}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Size</InputLabel>
              <Select
                label="Size"
                value={form.size}
                onChange={(e) => setForm((prev) => ({ ...prev, size: e.target.value as dt.EFEM_LOAD_PORT_SIZE }))}
              >
                {LOAD_PORT_SIZES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>{barcodeModuleLabel} (optional)</InputLabel>
              <Select
                label={`${barcodeModuleLabel} (optional)`}
                value={form.scannerSerial}
                onChange={(e) => setForm((prev) => ({ ...prev, scannerSerial: e.target.value }))}
              >
                <MenuItem value="">(none)</MenuItem>
                {allScanners.map((s) => {
                  const reassign = getScannerReassignState(s);
                  return (
                    <MenuItem
                      key={s.serialNumber}
                      value={s.serialNumber}
                      disabled={!reassign.selectable}
                    >
                      {s.serialNumber}
                      {reassign.assignmentLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setDialogOpen(false)}>Cancel</AppButton>
          <AppButton variant="contained" onClick={handleDialogApply}>
            {editingId ? 'Apply' : 'Add'}
          </AppButton>
        </DialogActions>
      </Dialog>
    </EfemSetupSection>
  );
}
